// A drum kit for the MIDI preview — so a drum lane auditions as drums, not
// as sines. General MIDI's percussion map decides what a pitch is (35/36
// kick, 38/40 snare, 42 closed hat, 46 open hat, toms by number, …), and
// each voice is a small synthesised hit: a pitched body that drops in pitch
// (kick, toms), a burst of filtered noise (snare, hats, cymbals), or both.
// Playback-only, like every preset: Live plays its own instrument, and
// nothing here touches the wire. The pitch → voice table is pure (tested);
// the scheduling half needs a context.

import type { MidiNoteEvent } from "@web-shared/lib/audio-engine";
import { registerVoice } from "@web-shared/lib/synth-voice";

export type DrumKind = "kick" | "snare" | "clap" | "sidestick" | "hatClosed" | "hatOpen" | "tom" | "crash" | "ride" | "bell" | "perc";

export interface DrumVoice {
  kind: DrumKind;
  /** The pitched body: starts at `startHz`, falls to `endHz` over `decaySec`. */
  body: { startHz: number; endHz: number; decaySec: number; level: number } | null;
  /** The noise burst: band- or high-passed at `filterHz`, over `decaySec`. */
  noise: { filterHz: number; bandpass: boolean; decaySec: number; level: number } | null;
}

const TOMS: Record<number, number> = { 41: 80, 43: 95, 45: 110, 47: 130, 48: 150, 50: 175 };

/** What a General MIDI percussion pitch sounds like here. Pitches outside
 * the map are short pitched blips at their own pitch, so a non-drum clip on
 * a kit lane still says something. */
export function kitVoice(pitch: number): DrumVoice {
  const p = Math.round(pitch);
  if (p === 35 || p === 36) {
    return { kind: "kick", body: { startHz: 150, endHz: 45, decaySec: 0.35, level: 1 }, noise: { filterHz: 2500, bandpass: false, decaySec: 0.015, level: 0.25 } };
  }
  if (p === 38 || p === 40) {
    return { kind: "snare", body: { startHz: 220, endHz: 160, decaySec: 0.12, level: 0.5 }, noise: { filterHz: 1800, bandpass: false, decaySec: 0.22, level: 0.7 } };
  }
  if (p === 37) return { kind: "sidestick", body: { startHz: 900, endHz: 700, decaySec: 0.05, level: 0.5 }, noise: { filterHz: 3000, bandpass: true, decaySec: 0.04, level: 0.4 } };
  if (p === 39) return { kind: "clap", body: null, noise: { filterHz: 1200, bandpass: true, decaySec: 0.2, level: 0.8 } };
  if (p === 42 || p === 44) return { kind: "hatClosed", body: null, noise: { filterHz: 7000, bandpass: false, decaySec: 0.06, level: 0.5 } };
  if (p === 46) return { kind: "hatOpen", body: null, noise: { filterHz: 6000, bandpass: false, decaySec: 0.45, level: 0.5 } };
  if (p in TOMS) return { kind: "tom", body: { startHz: TOMS[p]! * 1.6, endHz: TOMS[p]!, decaySec: 0.3, level: 0.9 }, noise: { filterHz: 2000, bandpass: false, decaySec: 0.02, level: 0.15 } };
  if (p === 49 || p === 52 || p === 55 || p === 57) return { kind: "crash", body: null, noise: { filterHz: 4500, bandpass: false, decaySec: 1.2, level: 0.45 } };
  if (p === 51 || p === 59) return { kind: "ride", body: { startHz: 3200, endHz: 3000, decaySec: 0.6, level: 0.12 }, noise: { filterHz: 6500, bandpass: false, decaySec: 0.7, level: 0.3 } };
  if (p === 53 || p === 56) return { kind: "bell", body: { startHz: p === 56 ? 800 : 1900, endHz: p === 56 ? 800 : 1900, decaySec: 0.3, level: 0.5 }, noise: null };
  if (p === 54) return { kind: "perc", body: null, noise: { filterHz: 8000, bandpass: true, decaySec: 0.12, level: 0.4 } };
  const hz = 440 * 2 ** ((p - 69) / 12);
  return { kind: "perc", body: { startHz: hz * 1.2, endHz: hz, decaySec: 0.15, level: 0.5 }, noise: null };
}

/** Output level for a hit: velocity-squared, like the synth's peak. */
export function drumLevel(velocity: number): number {
  const v = Math.max(0, Math.min(127, velocity)) / 127;
  return v * v;
}

/** How long a hit rings, so a scheduler knows when the voice is silent. */
export function drumLengthSec(voice: DrumVoice): number {
  return Math.max(voice.body?.decaySec ?? 0, voice.noise?.decaySec ?? 0) + 0.02;
}

const noiseBuffers = new WeakMap<BaseAudioContext, AudioBuffer>();

/** One second of white noise, made once per context — from a fixed seed, so
 * a kit renders the same every time (a mixdown, a loudness spec). */
export function noiseBuffer(ctx: BaseAudioContext): AudioBuffer {
  let buf = noiseBuffers.get(ctx);
  if (buf) return buf;
  buf = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate);
  const data = buf.getChannelData(0);
  let seed = 0x9e3779b9;
  for (let i = 0; i < data.length; i++) {
    // xorshift32
    seed ^= seed << 13;
    seed ^= seed >>> 17;
    seed ^= seed << 5;
    data[i] = (seed >>> 0) / 0x80000000 - 1;
  }
  noiseBuffers.set(ctx, buf);
  return buf;
}

/**
 * Schedule one hit at `when` into `out`, scaled by `trim` (the kit
 * preset's `level`). Returns the body oscillator (a
 * silent one when the voice is all noise) so callers can stop the voice by
 * the same handle the synth hands them; the noise goes with it.
 */
export function scheduleDrum(ctx: BaseAudioContext, note: MidiNoteEvent, when: number, out: AudioNode, trim = 1): OscillatorNode {
  const voice = kitVoice(note.pitch);
  const level = drumLevel(note.velocity) * trim;
  const length = drumLengthSec(voice);
  const osc = ctx.createOscillator();
  osc.type = "sine";
  const bodyAmp = ctx.createGain();
  if (voice.body) {
    osc.frequency.setValueAtTime(voice.body.startHz, when);
    osc.frequency.exponentialRampToValueAtTime(Math.max(20, voice.body.endHz), when + voice.body.decaySec);
    bodyAmp.gain.setValueAtTime(voice.body.level * level, when);
    bodyAmp.gain.exponentialRampToValueAtTime(0.001, when + voice.body.decaySec);
  } else {
    bodyAmp.gain.value = 0;
  }
  osc.connect(bodyAmp);
  bodyAmp.connect(out);

  const extras: AudioScheduledSourceNode[] = [];
  if (voice.noise) {
    const src = ctx.createBufferSource();
    src.buffer = noiseBuffer(ctx);
    src.loop = true;
    const filter = ctx.createBiquadFilter();
    filter.type = voice.noise.bandpass ? "bandpass" : "highpass";
    filter.frequency.value = voice.noise.filterHz;
    filter.Q.value = voice.noise.bandpass ? 1.5 : 0.7;
    const amp = ctx.createGain();
    amp.gain.setValueAtTime(voice.noise.level * level, when);
    amp.gain.exponentialRampToValueAtTime(0.001, when + voice.noise.decaySec);
    src.connect(filter);
    filter.connect(amp);
    amp.connect(out);
    src.start(when);
    src.stop(when + voice.noise.decaySec + 0.02);
    extras.push(src);
  }

  osc.start(when);
  osc.stop(when + length);
  osc.addEventListener("ended", () => {
    for (const n of extras) {
      try {
        n.stop();
      } catch {
        // already finished
      }
      n.disconnect();
    }
    bodyAmp.disconnect();
  });
  registerVoice(osc, [osc, ...extras]);
  return osc;
}
